<template>
    <div class="chat-page">
        <CCard>
            <CCardHeader>
                <strong>{{ chatMeta.title || 'Чат' }}</strong>
            </CCardHeader>
            <CCardBody>
                <div v-if="loading" class="table__preloader">
                    <CSpinner color="primary" />
                </div>
                <template v-else>
                    <div class="chat-page__layout">
                        <div class="chat-page__sidebar">
                            <div class="chat-page__customer">
                                <div class="chat-page__customer-title">Клиент</div>
                                <div class="chat-page__customer-row">
                                    <span>Имя</span>
                                    <strong>{{ customer.firstname }} {{ customer.lastname }}</strong>
                                </div>
                                <div class="chat-page__customer-row">
                                    <span>Телефон</span>
                                    <strong>{{ customer.phone || '—' }}</strong>
                                </div>
                                <div class="chat-page__customer-row">
                                    <span>Email</span>
                                    <strong>{{ customer.email || '—' }}</strong>
                                </div>
                                <div class="chat-page__customer-row">
                                    <span>ID</span>
                                    <strong>{{ customer.id }}</strong>
                                </div>
                                <div v-if="customer.orders" class="chat-page__customer-row">
                                    <span>Заказов</span>
                                    <strong>{{ customer.orders.count }}</strong>
                                </div>
                                <div class="chat-page__customer-row">
                                    <span>Последний заказ</span>
                                    <strong>{{ customer.last_order_date ? formatDate(customer.last_order_date) : '—' }}</strong>
                                </div>
                                <div class="chat-page__customer-row">
                                    <span>Статус</span>
                                    <strong>{{ formatChatStatus(chatMeta.status) }}</strong>
                                </div>
                                <div class="chat-page__customer-row">
                                    <span>Устройство</span>
                                    <strong>{{ formatCustomerDevice(customer) }}</strong>
                                </div>
                                <div class="chat-page__customer-row">
                                    <span>Версия приложения</span>
                                    <strong>{{ customer.appVersion || '—' }}</strong>
                                </div>
                                <div class="chat-page__customer-actions">
                                    <CButton color="link" class="p-0" @click="openCustomerCard">
                                        Карточка клиента
                                    </CButton>
                                    <CButton color="link" class="p-0" @click="openCustomerOrders">
                                        История заказов
                                    </CButton>
                                </div>
                            </div>
                        </div>

                        <div class="chat-page__main">
                            <div v-if="chat.items.length > 1" class="chat-summary-cards chat-summary-cards--horizontal">
                                <ChatSummaryCard
                                    v-for="thread in chat.items"
                                    :key="thread.chat_id"
                                    :chat="thread"
                                    :active="thread.chat_id === activeChatId"
                                    :show-customer="false"
                                    @select="thread => selectThread(thread.chat_id)"
                                />
                            </div>

                            <div ref="chatMessages" class="chat-page__messages">
                                <div
                                    v-for="message in chatMessages"
                                    :key="message.id"
                                    :class="[
                                        'chat-page__message',
                                        message.sender_type === 'admin'
                                            ? 'chat-page__message--admin'
                                            : message.sender_type === 'system'
                                                ? 'chat-page__message--system'
                                                : 'chat-page__message--customer'
                                    ]"
                                >
                                    <div v-if="message.attachments && message.attachments.length" class="chat-page__attachments">
                                        <div
                                            v-for="attachment in message.attachments"
                                            :key="attachment.id"
                                            class="chat-page__attachment"
                                        >
                                            <img
                                                v-if="isImageAttachment(attachment)"
                                                :src="attachment.url"
                                                :alt="attachment.original_name"
                                                class="chat-page__attachment-image"
                                            />
                                            <video
                                                v-else-if="isVideoAttachment(attachment)"
                                                :src="attachment.url"
                                                controls
                                                preload="metadata"
                                                class="chat-page__attachment-video"
                                            ></video>
                                        </div>
                                    </div>
                                    <div class="chat-page__message-text">
                                        <ChatMessageText :text="message.message" />
                                    </div>
                                    <div class="chat-page__message-time">
                                        <span>{{ formatChatDate(message.created_at) }}</span>
                                        <span v-if="message.edited_at" class="chat-page__message-edited">изменено</span>
                                        <ChatMessageEdit
                                            :message="message"
                                            :can-edit="canEditMessage(message)"
                                            :saving="editingMessageId === message.id"
                                            @save="saveEditedMessage"
                                        />
                                    </div>
                                </div>
                                <div v-if="!chatMessages.length" class="chat-page__empty">
                                    Нет сообщений
                                </div>
                            </div>

                            <div v-if="chat.pendingFiles.length" class="chat-page__pending">
                                <div
                                    v-for="(item, index) in chat.pendingFiles"
                                    :key="item.id"
                                    class="chat-page__pending-item"
                                >
                                    <div class="chat-page__pending-name">{{ item.file.name }}</div>
                                    <button
                                        type="button"
                                        class="chat-page__pending-remove"
                                        @click="removePendingFile(index)"
                                    >
                                        &times;
                                    </button>
                                </div>
                            </div>

                            <div class="chat-page__composer">
                                <CTextarea
                                    v-model.trim="chat.draft"
                                    rows="3"
                                    placeholder="Введите сообщение клиенту"
                                    style="margin-bottom: 0;"
                                    :disabled="!chat.connected || chat.sending || !activeChatId || isChatClosed"
                                    @keydown.enter.exact.prevent="sendChatMessage"
                                />
                                <div class="chat-page__composer-actions">
                                    <CButton
                                        color="success"
                                        class="mr-2"
                                        :disabled="!activeChatId || resolvingChat || isChatClosed"
                                        @click="resolveChat"
                                    >
                                        {{ resolvingChat ? 'Закрываем...' : 'Вопрос решен' }}
                                    </CButton>
                                    <ChatQuickReplyPicker
                                        :disabled="isAttachDisabled"
                                        :customer="customer"
                                        :chat="activeChatContext"
                                        @select="onQuickReplySelect"
                                    />
                                    <label class="chat-page__attach" :class="{ 'chat-page__attach--disabled': isAttachDisabled }">
                                        <input
                                            ref="fileInput"
                                            type="file"
                                            hidden
                                            multiple
                                            accept="image/*,video/*"
                                            :disabled="isAttachDisabled"
                                            @change="onFilesSelected"
                                        >
                                        📎
                                    </label>
                                    <CButton
                                        color="primary"
                                        class="chat-page__send"
                                        aria-label="Отправить сообщение"
                                        :disabled="!canSendMessage"
                                        @click="sendChatMessage"
                                    >
                                        <span class="chat-page__send-icon">➤</span>
                                    </CButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </CCardBody>
            <CCardFooter>
                <CButton color="primary" @click="goBack">Назад к списку</CButton>
            </CCardFooter>
        </CCard>
    </div>
</template>

<script>
    import Vue from 'vue';
    import moment from 'moment';
    import { io } from 'socket.io-client';
    import ChatSummaryCard from '@/components/chats/ChatSummaryCard';
    import ChatQuickReplyPicker from '@/components/chats/ChatQuickReplyPicker';
    import ChatMessageEdit from '@/components/chats/ChatMessageEdit';
    import ChatMessageText from '@/components/chats/ChatMessageText';
    const { DEVICES } = require('@/utils/devices');

    export default {
        name: 'Chat',
        components: {
            ChatSummaryCard,
            ChatQuickReplyPicker,
            ChatMessageEdit,
            ChatMessageText
        },
        data() {
            return {
                loading: true,
                customer: {
                    orders: {}
                },
                chatMeta: {},
                activeChatId: null,
                chat: {
                    socket: null,
                    connected: false,
                    items: [],
                    messagesByChat: {},
                    draft: '',
                    sending: false,
                    pendingFiles: []
                },
                resolvingChat: false,
                editingMessageId: null,
                currentAdminId: null
            };
        },
        computed: {
            chatMessages() {
                return this.chat.messagesByChat[this.activeChatId] || [];
            },
            isChatClosed() {
                return this.chatMeta?.status === 'closed';
            },
            canSendMessage() {
                return this.chat.connected
                    && !this.chat.sending
                    && !!this.activeChatId
                    && !this.isChatClosed
                    && (!!this.chat.draft || this.chat.pendingFiles.length > 0);
            },
            isAttachDisabled() {
                return !this.chat.connected || this.chat.sending || !this.activeChatId || this.isChatClosed;
            },
            activeChatContext() {
                const thread = (this.chat.items || []).find(item => +item.chat_id === +this.activeChatId);
                return {
                    title: thread?.title || this.chatMeta?.title || '',
                    order_id: thread?.order_id || this.chatMeta?.order_id || null
                };
            }
        },
        async mounted() {
            try {
                this.currentAdminId = Number(JSON.parse(localStorage.getItem('profile') || '{}').id) || null;
            } catch (e) {
                this.currentAdminId = null;
            }
            await this.loadChat();
            this.initChat();
        },
        beforeDestroy() {
            this.clearPendingFiles();
            if (this.chat.socket) {
                this.chat.socket.disconnect();
                this.chat.socket = null;
            }
        },
        methods: {
            getSocketBaseUrl() {
                const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || '';
                return apiBaseUrl
                    .replace(/\/api\/admin\/?$/, '')
                    .replace(/\/api\/?$/, '')
                    .replace(/\/admin\/?$/, '');
            },
            async loadChat() {
                this.loading = true;
                try {
                    const chatId = Number(this.$route.params.chatId);
                    const { data: chatMeta } = await Vue.axios.get(`/chats/${chatId}`);
                    this.chatMeta = chatMeta;
                    this.activeChatId = chatId;

                    const { data: customer } = await Vue.axios.get(`/customers/get/${chatMeta.customer_id}`);
                    this.customer = customer;
                    if (!this.customer.orders) {
                        this.customer.orders = {};
                    }

                    const { data: chatsData } = await Vue.axios.get(`/chats/customer/${chatMeta.customer_id}`);
                    this.chat.items = (chatsData.items || []).slice().sort((a, b) => new Date(b.last_message_at || b.updated_at || b.created_at || 0) - new Date(a.last_message_at || a.updated_at || a.created_at || 0));
                } catch (e) {
                    this.$root.$emit('showError', 'Не удалось загрузить чат');
                } finally {
                    this.loading = false;
                }
            },
            initChat() {
                const token = localStorage.getItem('user-token');
                if (!token) {
                    return;
                }

                this.chat.socket = io(this.getSocketBaseUrl(), {
                    path: '/socket.io',
                    reconnection: true,
                    transports: ['websocket'],
                    auth: {
                        role: 'admin',
                        token
                    },
                    query: {
                        role: 'admin',
                        token
                    }
                });

                this.chat.socket.on('connect', () => {
                    this.chat.connected = true;
                    if (this.activeChatId) {
                        this.fetchChatHistory(this.activeChatId);
                    }
                });

                this.chat.socket.on('disconnect', () => {
                    this.chat.connected = false;
                });

                this.chat.socket.on('connect_error', err => {
                    this.chat.connected = false;
                    this.$root.$emit('showError', `Не удалось подключиться к сокету чата: ${err && err.message ? err.message : 'unknown error'}`);
                });

                this.chat.socket.on('chat:message:new', message => {
                    if (+message.customer_id !== +this.customer.id) {
                        return;
                    }
                    this.upsertChatMessage(message);
                    this.updateChatFromMessage(message);
                    if (+message.chat_id === +this.activeChatId) {
                        this.markRead(message);
                        this.scrollChatToBottom();
                    }
                });

                this.chat.socket.on('chat:message:read', message => {
                    if (+message.customer_id !== +this.customer.id) {
                        return;
                    }
                    this.upsertChatMessage(message);
                    this.setChatUnreadCount(message.chat_id, 0);
                });

                this.chat.socket.on('chat:message:edited', message => {
                    if (+message.customer_id !== +this.customer.id) {
                        return;
                    }
                    this.upsertChatMessage(message);
                    this.updateChatPreviewFromEditedMessage(message);
                });

                this.chat.socket.on('chat:status:updated', summary => {
                    if (+summary.customer_id !== +this.customer.id) {
                        return;
                    }
                    this.applyChatStatusUpdate(summary);
                });

                this.chat.socket.on('chat:error', payload => {
                    const text = payload && payload.error ? payload.error : 'Ошибка чата';
                    this.$root.$emit('showError', text);
                });
            },
            selectThread(chatId) {
                this.activeChatId = Number(chatId);
                this.setChatUnreadCount(chatId, 0);
                this.$router.replace({
                    name: 'Chat',
                    params: {
                        chatId
                    }
                }).catch(() => {});
                if (this.chat.connected) {
                    this.fetchChatHistory(chatId);
                }
            },
            fetchChatHistory(chatId = this.activeChatId) {
                if (!this.chat.socket || !chatId) {
                    return;
                }
                this.chat.socket.emit('chat:history', {
                    chat_id: Number(chatId),
                    limit: 100,
                    offset: 0
                }, response => {
                    if (!response || !response.ok) {
                        return;
                    }
                    const history = response.messages || [];
                    this.$set(this.chat.messagesByChat, Number(chatId), []);
                    history.forEach(this.upsertChatMessage);
                    this.chatMessages.forEach(this.markRead);
                    this.setChatUnreadCount(chatId, 0);
                    this.scrollChatToBottom();
                });
            },
            onQuickReplySelect(text) {
                this.chat.draft = String(text || '');
            },
            canEditMessage(message) {
                return message
                    && message.sender_type === 'admin'
                    && this.currentAdminId
                    && Number(message.sender_id) === Number(this.currentAdminId);
            },
            saveEditedMessage({ messageId, message, done }) {
                if (!this.chat.socket || !messageId || this.editingMessageId) {
                    if (typeof done === 'function') {
                        done(false);
                    }
                    return;
                }
                this.editingMessageId = Number(messageId);
                this.chat.socket.emit('chat:message:edit', {
                    message_id: Number(messageId),
                    message
                }, response => {
                    this.editingMessageId = null;
                    if (!response || !response.ok) {
                        this.$root.$emit('showError', (response && response.error) || 'Не удалось изменить сообщение');
                        if (typeof done === 'function') {
                            done(false);
                        }
                        return;
                    }
                    this.upsertChatMessage(response.message);
                    this.updateChatPreviewFromEditedMessage(response.message);
                    if (typeof done === 'function') {
                        done(true);
                    }
                });
            },
            updateChatPreviewFromEditedMessage(message) {
                if (!message || !message.chat_id) {
                    return;
                }
                const messages = this.chat.messagesByChat[Number(message.chat_id)] || [];
                const last = messages[messages.length - 1];
                if (!last || +last.id !== +message.id) {
                    return;
                }
                const idx = this.chat.items.findIndex(item => +item.chat_id === +message.chat_id);
                if (idx < 0) {
                    return;
                }
                this.$set(this.chat.items, idx, {
                    ...this.chat.items[idx],
                    last_message: String(message.message || '').trim() || this.chat.items[idx].last_message,
                    last_message_at: message.edited_at || message.created_at
                });
            },
            async sendChatMessage() {
                if (!this.canSendMessage || !this.chat.socket) {
                    return;
                }
                this.chat.sending = true;
                try {
                    const attachmentIds = await this.uploadPendingFiles();
                    this.chat.socket.emit('chat:message', {
                        chat_id: Number(this.activeChatId),
                        message: this.chat.draft,
                        attachment_ids: attachmentIds
                    }, response => {
                        this.chat.sending = false;
                        if (!response || !response.ok) {
                            this.$root.$emit('showError', response && response.error ? response.error : 'Не удалось отправить сообщение');
                            return;
                        }
                        this.chat.draft = '';
                        this.clearPendingFiles();
                        this.scrollChatToBottom();
                    });
                } catch (e) {
                    this.chat.sending = false;
                    this.$root.$emit('showError', 'Не удалось загрузить вложения');
                }
            },
            async uploadPendingFiles() {
                const ids = [];
                for (const item of this.chat.pendingFiles) {
                    const formData = new FormData();
                    formData.append('chat_id', this.activeChatId);
                    formData.append('file', item.file);
                    const { data } = await Vue.axios.post('/chats/attachments', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                    if (data?.attachment?.id) {
                        ids.push(data.attachment.id);
                    }
                }
                return ids;
            },
            onFilesSelected(event) {
                const files = Array.from(event.target.files || []);
                files.forEach(file => {
                    if (!this.isAllowedMimeType(file.type)) {
                        this.$root.$emit('showError', 'Можно отправлять только фото и видео');
                        return;
                    }
                    if (file.size > 20 * 1000 * 1000) {
                        this.$root.$emit('showError', 'Размер файла не должен превышать 20 МБ');
                        return;
                    }
                    this.chat.pendingFiles.push({
                        id: `${Date.now()}-${file.name}-${this.chat.pendingFiles.length}`,
                        file
                    });
                });
                event.target.value = '';
            },
            removePendingFile(index) {
                this.chat.pendingFiles.splice(index, 1);
            },
            clearPendingFiles() {
                this.chat.pendingFiles = [];
            },
            markRead(message) {
                if (!message || !message.id || message.sender_type !== 'customer' || message.read_at || !this.chat.socket) {
                    return;
                }
                this.chat.socket.emit('chat:read', { message_id: message.id });
            },
            upsertChatMessage(message) {
                if (!message || !message.id || !message.chat_id) {
                    return;
                }
                const chatId = Number(message.chat_id);
                const messages = (this.chat.messagesByChat[chatId] || []).slice();
                const idx = messages.findIndex(item => +item.id === +message.id);
                if (idx >= 0) {
                    messages.splice(idx, 1, {
                        ...messages[idx],
                        ...message
                    });
                } else {
                    messages.push(message);
                }
                messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                this.$set(this.chat.messagesByChat, chatId, messages);
            },
            updateChatFromMessage(message) {
                if (!message || message.sender_type === 'system') {
                    return;
                }

                const idx = this.chat.items.findIndex(item => +item.chat_id === +message.chat_id);
                if (idx < 0) {
                    return;
                }
                const attachmentsCount = Array.isArray(message.attachments) ? message.attachments.length : 0;
                const lastMessage = String(message.message || '').trim() || (attachmentsCount > 1 ? `${attachmentsCount} вложения` : attachmentsCount === 1 ? 'Вложение' : '');
                this.$set(this.chat.items, idx, {
                    ...this.chat.items[idx],
                    last_message: lastMessage,
                    last_message_at: message.created_at,
                    last_sender_type: message.sender_type,
                    unread_count: (+message.chat_id === +this.activeChatId || message.sender_type !== 'customer')
                        ? 0
                        : (Number(this.chat.items[idx].unread_count) || 0) + 1
                });
                this.chat.items = this.chat.items.slice().sort((a, b) => new Date(b.last_message_at || b.updated_at || b.created_at || 0) - new Date(a.last_message_at || a.updated_at || a.created_at || 0));
            },
            setChatUnreadCount(chatId, count) {
                const idx = this.chat.items.findIndex(item => +item.chat_id === +chatId);
                if (idx < 0) {
                    return;
                }
                this.$set(this.chat.items, idx, {
                    ...this.chat.items[idx],
                    unread_count: count
                });
            },
            formatDate(value) {
                return moment(value).format('DD.MM.YYYY');
            },
            formatChatStatus(status) {
                return status === 'closed' ? 'Закрыт' : 'В работе';
            },
            formatCustomerDevice(customer) {
                if (!customer?.os) {
                    return '—';
                }

                const platform = DEVICES[customer.os] || customer.os;
                return customer.osVersion ? `${platform} ${customer.osVersion}` : platform;
            },
            formatChatDate(value) {
                if (!value) {
                    return '';
                }
                return moment(value).format('DD.MM.YYYY HH:mm');
            },
            isImageAttachment(attachment) {
                return String(attachment?.mime_type || '').startsWith('image/');
            },
            isVideoAttachment(attachment) {
                return String(attachment?.mime_type || '').startsWith('video/');
            },
            isAllowedMimeType(mimeType) {
                const value = String(mimeType || '');
                return value.startsWith('image/') || value.startsWith('video/');
            },
            scrollChatToBottom() {
                this.$nextTick(() => {
                    if (this.$refs.chatMessages) {
                        this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
                    }
                });
            },
            applyChatStatusUpdate(summary) {
                if (!summary?.chat_id) {
                    return;
                }

                if (+summary.chat_id === +this.activeChatId) {
                    this.chatMeta = {
                        ...this.chatMeta,
                        ...summary
                    };
                }

                const idx = this.chat.items.findIndex(item => +item.chat_id === +summary.chat_id);
                if (idx >= 0) {
                    this.$set(this.chat.items, idx, {
                        ...this.chat.items[idx],
                        ...summary
                    });
                }
            },
            async resolveChat() {
                if (!this.activeChatId || this.resolvingChat || this.isChatClosed) {
                    return;
                }

                this.resolvingChat = true;
                try {
                    const { data } = await Vue.axios.post(`/chats/${this.activeChatId}/close`);
                    this.applyChatStatusUpdate(data);
                } catch (e) {
                    this.$root.$emit('showError', 'Не удалось закрыть обращение');
                } finally {
                    this.resolvingChat = false;
                }
            },
            goBack() {
                this.$router.push({ name: 'Chats' });
            },
            openCustomerCard() {
                this.$router.push({
                    path: `/customers/${this.customer.id}`,
                    query: {
                        chat_id: this.activeChatId
                    }
                });
            },
            openCustomerOrders() {
                this.$router.push(`/customers/${this.customer.id}/orders`);
            }
        }
    };
</script>

<style scoped>
.chat-page__layout {
    display: flex;
    gap: 16px;
}

.chat-page__sidebar {
    width: 280px;
    flex-shrink: 0;
}

.chat-page__customer {
    border: 1px solid #e4e7ea;
    border-radius: 8px;
    padding: 16px;
    background: #ffffff;
}

.chat-page__customer-title {
    font-weight: 600;
    margin-bottom: 12px;
}

.chat-page__customer-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
    font-size: 14px;
}

.chat-page__customer-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    margin-top: 12px;
}

.chat-page__main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.chat-page__composer {
    margin-top: 12px;
}

.chat-page__composer-actions {
    display: flex;
    align-items: center;
}

.chat-page__attach {
    width: 36px;
    height: 36px;
    border: 1px solid #ced4da;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin-bottom: 0;
    margin-right: 8px;
}

.chat-page__attach--disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.chat-page__send {
    width: 36px;
    height: 36px;
    min-width: 36px;
    border-radius: 6px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.chat-page__send-icon {
    font-size: 14px;
    line-height: 1;
}

.chat-page__messages {
    flex: 1;
    min-height: 420px;
    max-height: calc(100vh - 360px);
    overflow-y: auto;
    border: 1px solid #e4e7ea;
    border-radius: 8px;
    padding: 10px;
    background: #ffffff;
}

.chat-page__message {
    width: fit-content;
    max-width: 80%;
    padding: 8px 10px;
    margin-bottom: 8px;
    border-radius: 8px;
}

.chat-page__message--admin {
    margin-left: auto;
    background: #321fdb;
    color: #ffffff;
}

.chat-page__message--customer {
    margin-right: auto;
    background: #f1f3f5;
}

.chat-page__message--system {
    margin-left: auto;
    margin-right: auto;
    background: #fff8e7;
    color: #6b4e16;
}

.chat-page__message-time {
    margin-top: 4px;
    font-size: 11px;
    opacity: 0.75;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
}

.chat-page__message-edited {
    font-style: italic;
}

.chat-page__attachments {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 8px;
}

.chat-page__attachment-image,
.chat-page__attachment-video {
    display: block;
    max-width: 240px;
    border-radius: 8px;
}

.chat-page__composer {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 12px;
}

.chat-page__empty {
    color: #8a93a2;
    text-align: center;
    padding: 24px 12px;
}

.chat-page__pending {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 10px 0 -10px 0;
}

.chat-page__pending-item {
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 100%;
    padding: 6px 10px;
    border-radius: 8px;
    background: #f3f4f6;
}

.chat-page__pending-name {
    font-size: 12px;
}

.chat-page__pending-remove {
    width: 20px;
    border: 0;
    background: transparent;
    font-size: 18px;
    line-height: 1;
}

@media (max-width: 991.98px) {
    .chat-page__layout {
        flex-direction: column;
    }

    .chat-page__main {
        order: 1;
    }

    .chat-page__sidebar {
        order: 2;
        width: 100%;
    }

    .chat-page__messages {
        min-height: 320px;
        max-height: calc(100vh - 280px);
    }
}
</style>
